// src/app/features/services/addService/step-two/index.tsx
import PaymentCards from 'app/features/payments/PaymentCards';
import { DashboardContainerHeader } from 'app/layout/dashboardLayout/BoxLayout';
import { useCreateCardMutation } from 'app/services/payments/cards';
import {
  useDeleteCartItemMutation,
  useGetCartItemsQuery,
} from 'app/services/payments/cart';
import {
  useCheckoutOrderMutation,
  useCreateOrderMutation,
} from 'app/services/payments/orders';
import { CartItem } from 'app/services/responseTypes';
import { AddIcon, PreviousIcon } from 'assets/icons';
import { CustomButton } from 'Components/Auth/CustomButton';
import DeleteButton from 'Components/Buttons/DeleteButton';
import { successToast, errorToast } from 'Components/Notifications';
import Spinner from 'Components/Spinner';
import { useEffect, useState } from 'react';
import { StepWizardChildProps } from 'react-step-wizard';
import CartItemCard from '../../cartItemCard';
import { IPromoCode } from 'app/services/promocodes';
import {
  PromoCodeForm,
  TotalOrder,
  useCartCheckoutTotalPrice,
} from 'app/features/questionnaires/checkout';
import Button from 'Components/Buttons/Button';

const AddServicesStepTwo: React.FC<Partial<StepWizardChildProps>> = ({
  previousStep,
}) => {
  const {
    data: Cart,
    isLoading: isLoadingCart,
    isSuccess: isSuccessCart,
  } = useGetCartItemsQuery('Service');
  const [CreateOrder, { isLoading: isLoadingCreateOrder }] =
    useCreateOrderMutation();
  const [
    CheckoutOrder,
    { isLoading: isLoadingCheckoutOrder, isSuccess: isSuccessCheckoutOrder },
  ] = useCheckoutOrderMutation();
  const [AddCard, { isLoading: isLoadingAddCard }] = useCreateCardMutation();

  const [promocode, setPromoCode] = useState<IPromoCode>();

  const { cartAmountWithpromoCodeApplied, discountAmount, totalCartAmount } =
    useCartCheckoutTotalPrice(Cart, promocode);

  const [sum, setSum] = useState<number>();

  const AddCardFn = () => {
    AddCard().then((res: any) => {
      sessionStorage.setItem('shouldRedirectToCheckout', JSON.stringify(true));
      window.location.replace(res?.data?.redirect_url);
    });
  };

  // Pay function that creates the order and redirects to payment
  const Pay = () => {
    const CardItemsIds = Cart?.cart_items.map((cart: { id: any }) => cart.id);
    CreateOrder({
      order: {
        cart_items_ids: CardItemsIds!,
        promo_code: promocode?.code,
      },
    })
      .then((res: any) => {
        CheckoutOrder(res?.data?.order.id)
          .then((res: any) => {
            if (res.data.redirect_url) {
              window.location.replace(res?.data?.redirect_url);
            }
          })
          .catch((e: any) => {
            errorToast('Please Try Again');
          });
      })
      .catch((e: { data: { message: string } }) => {
        errorToast(e.data.message);
      });
  };

  useEffect(() => {
    Cart &&
      setSum(
        Cart.cart_items
          .map((cart: any) => cart.price.amount_cents)
          .reduce((prev: any, curr: any) => prev + curr, 0) / 100,
      );
  }, [Cart, isSuccessCart]);

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="grid grid-cols-5 gap-8 md-max:w-full h-full md-max:grid-cols-1 pb-8">
        <div className="grid col-span-3 w-full border-r border-gray-100 h-full md-max:border-0 md-max:gap-2">
          <div>
            {/* Added a flex container to wrap the Add Card button and the new Go To Pay button */}
            <div className="flex items-center gap-2">
              <div className="max-w-min whitespace-nowrap">
                {isLoadingAddCard ? (
                  <Spinner bounce={false} />
                ) : (
                  <CustomButton
                    text="Add Card"
                    height="10"
                    prefix={<AddIcon />}
                    textSize="text-sm"
                    bgColor="white"
                    borderColor="green"
                    textColor="green"
                    onClick={AddCardFn}
                  />
                )}
              </div>
              <div className="max-w-min whitespace-nowrap">
                <CustomButton
                  text="Go to Pay"
                  height="10"
                  textSize="text-sm"
                  onClick={Pay}
                />
              </div>
            </div>
            <PaymentCards />
          </div>

          <div className="w-1/2 md-max:w-full">
            <CustomButton
              onClick={() => previousStep && previousStep()}
              prefix={<PreviousIcon />}
              bgColor="white"
              textColor="gray-dark"
              borderColor="gray-150"
            >
              <span className="text-sm w-full font-semibold">
                Previous Step
              </span>
            </CustomButton>
          </div>
        </div>
        <div className="flex flex-col col-span-2 justify-between gap-4 h-full">
          <div>
            <DashboardContainerHeader title="Your Subscriptions" />
            {isLoadingCheckoutOrder ||
            isLoadingCheckoutOrder ||
            isLoadingCart ? (
              <Spinner />
            ) : (
              isSuccessCart && <CartItemCard Cart={Cart} type="Service" />
            )}
          </div>
          <div>
            <div className="mt-8 mb-4">
              <div className="p-4 border-t border-gray-100">
                <PromoCodeForm Cart={Cart} onChange={setPromoCode} />
              </div>
              <TotalOrder cart={Cart} promocode={promocode}>
                <Button
                  theme="green-fill"
                  className="py-4"
                  disabled={totalCartAmount === 0}
                  onClick={Pay}
                  isLoading={
                    isLoadingCheckoutOrder ||
                    isLoadingCreateOrder ||
                    isSuccessCheckoutOrder
                  }
                >
                  Pay
                </Button>
              </TotalOrder>
            </div>
          </div>
        </div>
      </div>
      {/*
        The commented-out section below is an alternate layout.
        It remains for reference if needed.
      */}
      {/*
      <div className="w-full flex justify-between pb-5">
        <div className="w-1/4 md-max:w-full">
          <CustomButton
            onClick={() => previousStep && previousStep()}
            prefix={<PreviousIcon />}
            bgColor="white"
            textColor="gray-dark"
            borderColor="gray-dark"
          >
            <span className="text-sm w-full font-semibold">
              Previous Step
            </span>
          </CustomButton>
        </div>
        <div className="w-1/2 md-max:w-full">
          <CustomButton
            onClick={() => Pay()}
            text="Pay"
            loading={
              isLoadingCheckoutOrder ||
              isLoadingCreateOrder ||
              isSuccessCheckoutOrder
            }
          />
        </div>
      </div>
      */}
    </div>
  );
};

export default AddServicesStepTwo;
